import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { useNavigate } from "react-router-dom";
import {
    Users,
    Search,
    Building2,
    ChevronDown,
    ChevronRight as ChevronRightIcon,
    UserCog,
    FolderOpen,
} from "lucide-react";
import { EmployeeRow, FlatTable } from "@/components/EmployeesFlatTable";
import { TeamGenderPill } from "@/components/GenderControl";
import { WorkforceAnalytics } from "@/components/workforce/WorkforceAnalytics";

const VIEW_KEY = "odyssey.employees.view_mode";

const UNASSIGNED_KEY = "__unassigned__";

const Employees = () => {
    const { users, depts, ready, refreshUsers } = useData();
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({}); // { [deptId]: true/false }
    const { user } = useAuth();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState(() => {
        try { const v = window.localStorage.getItem(VIEW_KEY); return ["flat", "analytics"].includes(v) ? v : "grouped"; } catch { return "grouped"; }
    });
    const toggleViewMode = (next) => {
        setViewMode(next);
        try { window.localStorage.setItem(VIEW_KEY, next); } catch { /* ignore */ }
    };

    const managerName = (id) => users.find((u) => u.user_id === id)?.name || "—";

    const filtered = useMemo(() => {
        if (!q) return users;
        const s = q.toLowerCase();
        return users.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(s) ||
                (u.email || "").toLowerCase().includes(s) ||
                (u.designation || "").toLowerCase().includes(s),
        );
    }, [users, q]);

    // Group by department. A user appears under their **home** department
    // and — if they have `managed_department_ids` — under every one of
    // those managed departments too, tagged with `_asAdditional=true` so
    // the row can show a subtle "+manages" indicator instead of implying
    // they live there.
    const groups = useMemo(() => {
        const map = {};
        for (const u of filtered) {
            const homeKey = u.department_id || UNASSIGNED_KEY;
            (map[homeKey] = map[homeKey] || []).push(u);
            const managed = Array.isArray(u.managed_department_ids) ? u.managed_department_ids : [];
            for (const did of managed) {
                if (!did || did === u.department_id) continue;
                (map[did] = map[did] || []).push({ ...u, _asAdditional: true });
            }
        }
        // Ordered: known depts (alphabetical), then unassigned
        const ordered = [];
        const sortedDepts = [...depts].sort((a, b) => a.name.localeCompare(b.name));
        for (const d of sortedDepts) {
            if (map[d.id]?.length) {
                ordered.push({ id: d.id, name: d.name, description: d.description, members: map[d.id] });
            }
        }
        if (map[UNASSIGNED_KEY]?.length) {
            ordered.push({ id: UNASSIGNED_KEY, name: "Unassigned", description: "Employees without a department", members: map[UNASSIGNED_KEY] });
        }
        return ordered;
    }, [filtered, depts]);

    // When searching, auto-expand matching groups; otherwise keep user's state (default collapsed)
    useEffect(() => {
        if (q.trim()) {
            const next = {};
            for (const g of groups) next[g.id] = true;
            setExpanded(next);
        }
    }, [q, groups]);

    const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
    const expandAll = () => setExpanded(groups.reduce((acc, g) => ({ ...acc, [g.id]: true }), {}));
    const collapseAll = () => setExpanded({});

    return (
        <div className="space-y-6" data-testid="employees-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">People</div>
                    <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                        <Users className="h-7 w-7 text-[#2EB4AC]" />
                        Employees
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {`${user?.role === "super_admin" ? "All employees across the organization" : "Your team"}, ${viewMode === "flat" ? "in one sortable list" : viewMode === "analytics" ? "workforce, tenure, hiring and milestone analytics" : "grouped by department"}`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1 text-xs" data-testid="employees-view-toggle">
                        {[["grouped", "Grouped"], ["flat", "Flat"], ["analytics", "Analytics"]].map(([k, l]) => (
                            <button
                                key={k}
                                onClick={() => toggleViewMode(k)}
                                data-testid={`employees-view-${k}`}
                                className={`px-2.5 h-7 rounded font-semibold transition-colors ${viewMode === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
                            >{l}</button>
                        ))}
                    </div>
                    {viewMode === "grouped" && <>
                    <button
                        onClick={expandAll}
                        data-testid="expand-all-depts"
                        className="h-10 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium text-slate-700 transition-colors"
                    >
                        Expand all
                    </button>
                    <button
                        onClick={collapseAll}
                        data-testid="collapse-all-depts"
                        className="h-10 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium text-slate-700 transition-colors"
                    >
                        Collapse all
                    </button>
                    </>}
                </div>
            </div>

            {viewMode !== "analytics" && <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        data-testid="employee-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email, or designation…"
                        className="h-11 w-full pl-10 pr-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none transition-all"
                    />
                </div>
            </div>}

            {!ready ? (
                <div className="space-y-3">
                    {[0,1,2,3].map((i) => (
                        <div key={i} className="h-14 bg-white border border-slate-200 rounded-lg shadow-sm animate-pulse flex items-center px-5 gap-3">
                            <div className="h-4 w-4 bg-slate-100 rounded" />
                            <div className="h-9 w-9 bg-slate-100 rounded-lg" />
                            <div className="h-4 w-48 bg-slate-100 rounded" />
                            <div className="ml-auto h-5 w-10 bg-slate-100 rounded-full" />
                        </div>
                    ))}
                </div>
            ) : viewMode === "analytics" ? (
                <WorkforceAnalytics users={users} depts={depts} />
            ) : groups.length === 0 ? (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                    <FolderOpen className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                    <p className="mt-3 font-heading font-semibold text-slate-900">No employees found</p>
                    <p className="text-slate-500 text-sm mt-1">Try a different search term</p>
                </div>
            ) : viewMode === "flat" ? (
                <FlatTable rows={filtered} users={users} depts={depts} managerName={managerName} navigate={navigate} canEditGender={user?.role === "super_admin"} onSaved={refreshUsers} />
            ) : (
                <div className="space-y-3">
                    {groups.map((g) => {
                        const isOpen = !!expanded[g.id];
                        const managerCount = g.members.filter((m) => m.role === "manager" || m.role === "super_admin").length;
                        const isUnassigned = g.id === UNASSIGNED_KEY;
                        return (
                            <div
                                key={g.id}
                                data-testid={`dept-group-${g.id}`}
                                className={`bg-white border rounded-lg shadow-sm overflow-hidden transition-colors ${
                                    isUnassigned ? "border-dashed border-slate-300" : "border-slate-200"
                                }`}
                            >
                                <button
                                    onClick={() => toggle(g.id)}
                                    data-testid={`dept-toggle-${g.id}`}
                                    className="w-full flex items-center justify-between px-5 h-14 text-left hover:bg-slate-50 transition-colors"
                                    aria-expanded={isOpen}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-5 w-5 text-slate-400 flex items-center justify-center">
                                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                                        </div>
                                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            isUnassigned ? "bg-slate-100 text-slate-500 border border-slate-200" : "bg-teal-50 text-[#25908A] border border-teal-100"
                                        }`}>
                                            <Building2 className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-heading font-semibold text-slate-900 truncate">{g.name}</div>
                                            {g.description && <div className="text-xs text-slate-500 truncate">{g.description}</div>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <TeamGenderPill members={g.members} testid={`dept-gender-${g.id}`} />
                                        {managerCount > 0 && (
                                            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                                                <UserCog className="h-3 w-3" /> {managerCount}
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-teal-50 text-[#25908A] border-teal-100">
                                            <Users className="h-3 w-3" /> {g.members.length}
                                        </span>
                                    </div>
                                </button>

                                {isOpen && (
                                    <div className="border-t border-slate-200 overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-slate-50 border-b border-slate-100">
                                                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                                    <th className="px-5 py-2.5">Name</th>
                                                    <th className="px-5 py-2.5 hidden lg:table-cell">Manager</th>
                                                    <th className="px-5 py-2.5">Role</th>
                                                    <th className="px-5 py-2.5"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {g.members
                                                    .slice()
                                                    .sort((a, b) => {
                                                        // Managers/admins first, then alphabetical
                                                        const ra = a.role === "employee" ? 2 : a.role === "manager" ? 1 : 0;
                                                        const rb = b.role === "employee" ? 2 : b.role === "manager" ? 1 : 0;
                                                        if (ra !== rb) return ra - rb;
                                                        return (a.name || "").localeCompare(b.name || "");
                                                    })
                                                    .map((u) => <EmployeeRow key={u.user_id} u={u} users={users} managerName={managerName} navigate={navigate} />)}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Employees;
